import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, compressImage, formatApiErrorDetail } from "@/lib/api";
import { PRIORITY_LABELS, RISK_LABELS, RISK_COLORS } from "@/lib/labels";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Camera, Check, X, Minus, ChevronRight, CheckCircle2, Zap, Trash2, Rat, Plus, AlertTriangle, Footprints } from "lucide-react";

function PhotoStrip({ photos, onRemove }) {
  if (!photos?.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {photos.map((p, i) => (
        <div key={i} className="relative group">
          <img src={p} alt={`evidência ${i + 1}`} className="h-14 w-14 rounded-md object-cover border border-border" />
          <button onClick={() => onRemove(i)}
            className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <X size={10} />
          </button>
        </div>
      ))}
    </div>
  );
}

function PhotoInput({ id, onFiles, label = "Adicionar fotos" }) {
  return (
    <label htmlFor={id} className="inline-flex items-center gap-1.5 text-xs text-primary cursor-pointer hover:underline">
      <Camera size={13} /> {label}
      <input id={id} type="file" accept="image/*" multiple className="hidden"
        onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} />
    </label>
  );
}

const EMPTY_FEZES = { quantity: 1, location: "", photos: [], notes: "" };

export default function ElectricInspectionFlow({ rooms, startIndex = 0, template, onExit, onFinished }) {
  const [idx, setIdx] = useState(startIndex);
  const [answers, setAnswers] = useState({});
  const [generalPhotos, setGeneralPhotos] = useState([]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState([]);
  const [done, setDone] = useState(false);
  const [rodentFound, setRodentFound] = useState(null);
  const [captures, setCaptures] = useState([]);
  const [fezes, setFezes] = useState(null);
  const [fezesData, setFezesData] = useState(EMPTY_FEZES);
  const [traces, setTraces] = useState([]);
  const [tracesPhotos, setTracesPhotos] = useState([]);
  const [tracesNotes, setTracesNotes] = useState("");
  const [species, setSpecies] = useState([]);
  const [vestigioTypes, setVestigioTypes] = useState([]);
  const [gps, setGps] = useState(null);
  const room = rooms[idx];

  useEffect(() => {
    api.get("/electric-rooms/species").then(({ data }) => setSpecies(data)).catch(() => {});
    api.get("/electric-rooms/vestigio-types").then(({ data }) => setVestigioTypes(data)).catch(() => {});
  }, []);

  const emptyCapture = () => ({ species_id: "", species_name: "", situation: "morto", quantity: 1, photos: [], notes: "" });
  const setCap = (i, patch) => setCaptures((cs) => cs.map((c, j) => (j === i ? { ...c, ...patch } : c)));

  const addCapPhotos = async (i, files) => {
    const arr = [];
    for (const f of Array.from(files || [])) arr.push(await compressImage(f));
    setCaptures((cs) => cs.map((c, j) => (j === i ? { ...c, photos: [...c.photos, ...arr].slice(0, 10) } : c)));
  };

  const captureGpsSilent = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => {},
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const setItem = (key, patch) => setAnswers((a) => ({ ...a, [key]: { ...(a[key] || {}), ...patch } }));

  const setRodent = (val) => {
    setRodentFound(val);
    if (val) {
      setCaptures((cs) => (cs.length ? cs : [emptyCapture()]));
      captureGpsSilent();
      setAnswers((a) => {
        const next = { ...a };
        for (const t of template.checklist) {
          if (t.rodent_access) next[t.key] = { ...(next[t.key] || {}), status: "nao_conforme", severity: t.severity || "alta", auto: true };
        }
        return next;
      });
    } else {
      setCaptures([]);
      setAnswers((a) => {
        const next = { ...a };
        for (const t of template.checklist) {
          if (t.rodent_access && next[t.key]?.auto) next[t.key] = { ...next[t.key], status: undefined, auto: false };
        }
        return next;
      });
    }
  };

  const toggleTrace = (v) => {
    setTraces((ts) => {
      const exists = ts.find((t) => t.type_id === v.id);
      if (exists) return ts.filter((t) => t.type_id !== v.id);
      captureGpsSilent();
      return [...ts, { type_id: v.id, label: v.name, detail: "" }];
    });
  };

  const addItemPhotos = async (key, files) => {
    const arr = [];
    for (const f of Array.from(files || [])) arr.push(await compressImage(f));
    setAnswers((a) => ({ ...a, [key]: { ...(a[key] || {}), photos: [...((a[key] || {}).photos || []), ...arr].slice(0, 10) } }));
  };

  const addGeneralPhotos = async (files) => {
    const arr = [];
    for (const f of Array.from(files || [])) arr.push(await compressImage(f));
    setGeneralPhotos((p) => [...p, ...arr].slice(0, 10));
  };

  const addFezesPhotos = async (files) => {
    const arr = [];
    for (const f of Array.from(files || [])) arr.push(await compressImage(f));
    setFezesData((d) => ({ ...d, photos: [...d.photos, ...arr].slice(0, 10) }));
  };

  const addTracesPhotos = async (files) => {
    const arr = [];
    for (const f of Array.from(files || [])) arr.push(await compressImage(f));
    setTracesPhotos((p) => [...p, ...arr].slice(0, 10));
  };

  const submit = async () => {
    const checklist = [];
    for (const t of template.checklist) {
      const a = answers[t.key] || {};
      if (t.required && !a.status) return toast.error(`Responda o item: ${t.label}`);
      checklist.push({ key: t.key, label: t.label, status: a.status || "nao_aplicavel", severity: a.severity || t.severity || "media", notes: a.notes || "", photos: a.photos || [] });
    }
    const minP = template.min_photos_nc || 1;
    for (const c of checklist) {
      const autoLocked = answers[c.key]?.auto && rodentFound === true;
      const t = template.checklist.find((x) => x.key === c.key) || {};
      // Foto por pergunta: config canónica photo_required.
      if (t.photo_required && c.photos.length === 0 && !autoLocked)
        return toast.error(`Foto obrigatória para a pergunta: ${c.label}`);
      if (c.status === "nao_conforme" && c.photos.length < minP && !autoLocked)
        return toast.error(`Evidência fotográfica obrigatória no item não conforme: ${c.label}`);
    }
    const totalPhotos = generalPhotos.length + checklist.reduce((s, c) => s + c.photos.length, 0);
    if (totalPhotos === 0 && !rodentFound) return toast.error("Anexe pelo menos 1 foto da sala");
    if (rodentFound === null) return toast.error("Informe se foi encontrado roedor durante a inspeção");
    if (rodentFound) {
      for (const c of captures) {
        const sp = species.find((s) => s.id === c.species_id);
        if (!c.species_id) return toast.error("Selecione a espécie do roedor capturado");
        if (sp?.name?.startsWith("Outro") && !c.species_name.trim()) return toast.error("Informe a espécie do roedor (campo 'Outro')");
        if (!c.quantity || c.quantity < 1) return toast.error("Quantidade de roedores deve ser no mínimo 1");
        if (!c.photos.length) return toast.error("Foto obrigatória para cada captura de roedor");
      }
    }
    if (fezes === null) return toast.error("Informe se foram encontradas fezes de roedor na sala");
    if (fezes) {
      if (!fezesData.quantity || fezesData.quantity < 1) return toast.error("Informe a quantidade aproximada de fezes");
      if (!fezesData.photos.length) return toast.error("Foto obrigatória para o registro de fezes");
    }
    for (const t of traces) {
      const v = vestigioTypes.find((x) => x.id === t.type_id);
      if (v?.requires_detail && !t.detail.trim()) return toast.error(`Descreva o vestígio "${t.label}"`);
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/electric-rooms/${room.id}/inspections`, {
        checklist, general_photos: generalPhotos, notes,
        rodent_found: rodentFound === true, captures: rodentFound ? captures : [],
        droppings: { found: fezes === true, quantity: fezesData.quantity, location: fezesData.location, photos: fezes ? fezesData.photos : [], notes: fezesData.notes },
        traces, traces_photos: tracesPhotos, traces_notes: tracesNotes,
        gps,
      });
      setResults((r) => [...r, { room: room.name, risk: data.risk_level, ncs: data.nc_count, occurrences: data.occurrences, rodents: data.rodents_total || 0, fezes: fezes === true, vestigios: traces.length }]);
      toast.success(`Sala "${room.name}" inspecionada — ${RISK_LABELS[data.risk_level]}${data.occurrences.length ? ` · ${data.occurrences.length} ocorrência(s) gerada(s)` : ""}`);
      if (idx + 1 < rooms.length) {
        setIdx(idx + 1);
        setAnswers({});
        setGeneralPhotos([]);
        setNotes("");
        setRodentFound(null);
        setCaptures([]);
        setFezes(null);
        setFezesData(EMPTY_FEZES);
        setTraces([]);
        setTracesPhotos([]);
        setTracesNotes("");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        setDone(true);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="inspection-summary">
        <div className="text-center pt-6">
          <CheckCircle2 size={44} className="mx-auto text-emerald-400" />
          <h2 className="font-heading text-2xl font-bold mt-3">Inspeção concluída</h2>
          <p className="text-sm text-muted-foreground mt-1">{results.length} sala(s) inspecionada(s) · {results.reduce((s, r) => s + r.occurrences.length, 0)} ocorrência(s) Astral gerada(s) automaticamente</p>
        </div>
        <div className="space-y-2">
          {results.map((r, i) => (
            <Card key={i} className="p-4 flex items-center justify-between gap-3 border-border bg-card" data-testid={`summary-room-${i}`}>
              <div className="flex items-center gap-3 min-w-0">
                <Zap size={16} className="text-amber-400 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{r.room}</p>
                  {r.occurrences.length > 0 && <p className="text-xs text-muted-foreground truncate">Ocorrências: {r.occurrences.join(", ")}</p>}
                </div>
              </div>
              <Badge variant="outline" className={RISK_COLORS[r.risk]}>
                {RISK_LABELS[r.risk]} · {r.ncs} NC{r.rodents > 0 ? ` · ${r.rodents} roedor(es)` : ""}{r.fezes ? " · fezes" : ""}{r.vestigios > 0 ? ` · ${r.vestigios} vestígio(s)` : ""}
              </Badge>
            </Card>
          ))}
        </div>
        <Button className="w-full" onClick={onFinished} data-testid="inspection-finish-btn">Encerrar e voltar ao Mapa de Risco</Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="inspection-flow">
      <button onClick={onExit} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="inspection-exit-btn">
        <ArrowLeft size={15} /> Sair da inspeção
      </button>

      <div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2" data-testid="inspection-room-name">
            <Zap size={18} className="text-amber-400" /> {room.name}{room.code ? <span className="text-sm text-muted-foreground font-normal">({room.code})</span> : null}
          </h2>
          <Badge variant="outline" data-testid="inspection-progress">Sala {idx + 1} de {rooms.length}</Badge>
        </div>
        <div className="h-1.5 rounded-full bg-muted mt-3">
          <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${((idx) / rooms.length) * 100}%` }} />
        </div>
      </div>

      <div className="space-y-3">
        {template.checklist.map((t) => {
          const a = answers[t.key] || {};
          const locked = !!t.rodent_access && rodentFound === true;
          return (
            <Card key={t.key} className="p-4 border-border bg-card" data-testid={`check-item-${t.key}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-sm font-medium flex items-center gap-1.5">
                  {t.label}
                  {t.required && (
                    <span className="text-[10px] font-semibold text-primary bg-primary/10 rounded px-1.5 py-0.5" data-testid={`check-required-badge-${t.key}`}>Obrigatória</span>
                  )}
                  {t.photo_required && (
                    <span className="text-[10px] font-semibold text-amber-600 bg-amber-500/10 rounded px-1.5 py-0.5" data-testid={`check-photo-badge-${t.key}`}>📸 Foto</span>
                  )}
                </p>
                <div className="flex gap-1.5">
                  <button onClick={() => !locked && setItem(t.key, { status: "conforme" })} disabled={locked}
                    className={`px-2.5 py-1.5 rounded-md text-xs font-semibold border flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${a.status === "conforme" ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/40" : "border-border text-muted-foreground hover:bg-accent"}`}
                    data-testid={`check-${t.key}-conforme`}>
                    <Check size={12} /> Conforme
                  </button>
                  <button onClick={() => !locked && setItem(t.key, { status: "nao_conforme", severity: a.severity || t.severity })} disabled={locked}
                    className={`px-2.5 py-1.5 rounded-md text-xs font-semibold border flex items-center gap-1 transition-colors disabled:cursor-not-allowed ${a.status === "nao_conforme" ? "bg-red-500/20 text-red-400 border-red-500/40" : "border-border text-muted-foreground hover:bg-accent"}`}
                    data-testid={`check-${t.key}-nc`}>
                    <X size={12} /> Não conforme
                  </button>
                  <button onClick={() => !locked && setItem(t.key, { status: "nao_aplicavel" })} disabled={locked}
                    className={`px-2.5 py-1.5 rounded-md text-xs font-semibold border flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${a.status === "nao_aplicavel" ? "bg-slate-500/20 text-slate-400 border-slate-500/40" : "border-border text-muted-foreground hover:bg-accent"}`}
                    data-testid={`check-${t.key}-na`}>
                    <Minus size={12} /> N/A
                  </button>
                </div>
              </div>
              {t.photo_required && (
                <div className="mt-3 pt-3 border-t border-border/60 space-y-2.5" data-testid={`photo-required-panel-${t.key}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-primary font-semibold flex items-center gap-1.5">
                      <Camera size={13} /> Foto obrigatória *
                    </span>
                    <PhotoInput id={`photo-req-${t.key}`} onFiles={(f) => addItemPhotos(t.key, f)}
                      label={a.photos?.length ? "Adicionar mais fotos" : "Anexar foto"} />
                  </div>
                  <PhotoStrip photos={a.photos} onRemove={(i) => setItem(t.key, { photos: a.photos.filter((_, j) => j !== i) })} />
                </div>
              )}
              {locked && (
                <div className="mt-2.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300 flex items-start gap-2" data-testid={`auto-rule-msg-${t.key}`}>
                  <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                  <span>Foi identificado roedor na sala. O sistema classificou automaticamente o item "{t.label}" como <strong>Não Conforme</strong>, pois a presença do animal indica a existência de um ponto de acesso. Você pode complementar com observações, mas a classificação não pode ser alterada enquanto houver registro de roedor.</span>
                </div>
              )}
              {a.status === "nao_conforme" && (
                <div className="mt-3 pt-3 border-t border-border/60 space-y-2.5" data-testid={`nc-panel-${t.key}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Severidade:</span>
                      <Select value={a.severity || t.severity} onValueChange={(v) => !locked && setItem(t.key, { severity: v })} disabled={locked}>
                        <SelectTrigger className="h-8 w-28 text-xs" data-testid={`nc-severity-${t.key}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(PRIORITY_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <PhotoInput id={`photo-${t.key}`} onFiles={(f) => addItemPhotos(t.key, f)}
                      label={locked ? "Foto do ponto de acesso (opcional — a foto da captura será usada como evidência)" : "Foto da evidência (obrigatória) *"} />
                  </div>
                  <Textarea rows={2} placeholder="Observações da não conformidade..." className="resize-none text-sm"
                    value={a.notes || ""} onChange={(e) => setItem(t.key, { notes: e.target.value })} data-testid={`nc-notes-${t.key}`} />
                  <PhotoStrip photos={a.photos} onRemove={(i) => setItem(t.key, { photos: a.photos.filter((_, j) => j !== i) })} />
                </div>
              )}
            </Card>
          );
        })}
      </div>

      <Card className="p-4 border-border bg-card space-y-3" data-testid="rodent-section">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm font-medium flex items-center gap-2">
            <Rat size={15} className="text-rose-400" /> Foi visualizado roedor durante a inspeção? *
          </p>
          <div className="flex gap-1.5">
            <button onClick={() => setRodent(true)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${rodentFound === true ? "bg-rose-500/20 text-rose-400 border-rose-500/40" : "border-border text-muted-foreground hover:bg-accent"}`}
              data-testid="rodent-yes-btn">Sim</button>
            <button onClick={() => setRodent(false)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${rodentFound === false ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/40" : "border-border text-muted-foreground hover:bg-accent"}`}
              data-testid="rodent-no-btn">Não</button>
          </div>
        </div>
        {rodentFound && (
          <div className="space-y-3">
            {captures.map((c, i) => {
              const sp = species.find((s) => s.id === c.species_id);
              return (
                <div key={i} className="rounded-md border border-rose-500/30 bg-rose-500/5 p-3 space-y-2.5" data-testid={`capture-entry-${i}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-rose-300 uppercase tracking-wide">Captura {i + 1}</p>
                    {captures.length > 1 && (
                      <button onClick={() => setCaptures((cs) => cs.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-red-400" data-testid={`capture-remove-${i}`}>
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    <Select value={c.species_id} onValueChange={(v) => { const s = species.find((x) => x.id === v); setCap(i, { species_id: v, species_name: s && !s.name.startsWith("Outro") ? s.name : "" }); }}>
                      <SelectTrigger className="h-9" data-testid={`capture-species-${i}`}><SelectValue placeholder="Espécie *" /></SelectTrigger>
                      <SelectContent>
                        {species.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.scientific_name ? ` (${s.scientific_name})` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={c.situation} onValueChange={(v) => setCap(i, { situation: v })}>
                      <SelectTrigger className="h-9" data-testid={`capture-situation-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vivo">Vivo</SelectItem>
                        <SelectItem value="morto">Morto</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input type="number" min={1} value={c.quantity} onChange={(e) => setCap(i, { quantity: Math.max(1, parseInt(e.target.value || "1")) })}
                      className="h-9" placeholder="Quantidade *" data-testid={`capture-quantity-${i}`} />
                  </div>
                  {sp?.name?.startsWith("Outro") && (
                    <Input value={c.species_name} onChange={(e) => setCap(i, { species_name: e.target.value })}
                      placeholder="Informe a espécie *" className="h-9" data-testid={`capture-custom-species-${i}`} />
                  )}
                  <PhotoInput id={`capture-photo-${i}`} onFiles={(f) => addCapPhotos(i, f)} label="Foto da captura (obrigatória) *" />
                  <PhotoStrip photos={c.photos} onRemove={(j) => setCap(i, { photos: c.photos.filter((_, k) => k !== j) })} />
                  <Textarea rows={2} placeholder="Observações da captura..." className="resize-none text-sm"
                    value={c.notes} onChange={(e) => setCap(i, { notes: e.target.value })} data-testid={`capture-notes-${i}`} />
                </div>
              );
            })}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <button onClick={() => setCaptures((cs) => [...cs, emptyCapture()])} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="capture-add-btn">
                <Plus size={12} /> Adicionar outra captura
              </button>
              <span className="text-[11px] text-muted-foreground" data-testid="capture-gps-info">
                {gps ? `GPS registrado: ${gps.lat.toFixed(4)}, ${gps.lng.toFixed(4)}` : "GPS não disponível (permissão negada ou indisponível)"}
              </span>
            </div>
          </div>
        )}
      </Card>

      <Card className="p-4 border-border bg-card space-y-3" data-testid="bio-evidence-section">
        <p className="text-sm font-semibold flex items-center gap-2">
          <Footprints size={15} className="text-orange-400" /> Evidências Biológicas
        </p>

        <div className="rounded-md border border-border p-3 space-y-2.5" data-testid="fezes-section">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm font-medium">Foram encontradas fezes de roedor? *</p>
            <div className="flex gap-1.5">
              <button onClick={() => { setFezes(true); captureGpsSilent(); }}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${fezes === true ? "bg-amber-500/20 text-amber-400 border-amber-500/40" : "border-border text-muted-foreground hover:bg-accent"}`}
                data-testid="fezes-yes-btn">Sim</button>
              <button onClick={() => setFezes(false)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${fezes === false ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/40" : "border-border text-muted-foreground hover:bg-accent"}`}
                data-testid="fezes-no-btn">Não</button>
            </div>
          </div>
          {fezes && (
            <div className="space-y-2.5" data-testid="fezes-panel">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <Input type="number" min={1} value={fezesData.quantity}
                  onChange={(e) => setFezesData((d) => ({ ...d, quantity: Math.max(1, parseInt(e.target.value || "1")) }))}
                  className="h-9" placeholder="Quantidade aproximada *" data-testid="fezes-quantity" />
                <Input value={fezesData.location} onChange={(e) => setFezesData((d) => ({ ...d, location: e.target.value }))}
                  className="h-9" placeholder="Local encontrado (ex.: atrás do painel)" data-testid="fezes-location" />
              </div>
              <PhotoInput id="fezes-photos" onFiles={addFezesPhotos} label="Foto da evidência (obrigatória) *" />
              <PhotoStrip photos={fezesData.photos} onRemove={(i) => setFezesData((d) => ({ ...d, photos: d.photos.filter((_, j) => j !== i) }))} />
              <Textarea rows={2} placeholder="Observações sobre as fezes encontradas..." className="resize-none text-sm"
                value={fezesData.notes} onChange={(e) => setFezesData((d) => ({ ...d, notes: e.target.value }))} data-testid="fezes-notes" />
            </div>
          )}
        </div>

        <div className="rounded-md border border-border p-3 space-y-2.5" data-testid="vestigios-section">
          <p className="text-sm font-medium">Vestígios identificados <span className="text-xs text-muted-foreground font-normal">(marque todos que se aplicam)</span></p>
          <div className="flex flex-wrap gap-1.5">
            {vestigioTypes.map((v) => {
              const sel = traces.some((t) => t.type_id === v.id);
              return (
                <button key={v.id} onClick={() => toggleTrace(v)} data-testid={`trace-toggle-${v.id}`}
                  className={`px-2.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${sel ? "bg-orange-500/20 text-orange-400 border-orange-500/40" : "border-border text-muted-foreground hover:bg-accent"}`}>
                  {v.name}
                </button>
              );
            })}
            {vestigioTypes.length === 0 && <p className="text-xs text-muted-foreground">Nenhum vestígio parametrizado no ERP.</p>}
          </div>
          {traces.filter((t) => vestigioTypes.find((v) => v.id === t.type_id)?.requires_detail).map((t) => (
            <Input key={t.type_id} value={t.detail}
              onChange={(e) => setTraces((ts) => ts.map((x) => (x.type_id === t.type_id ? { ...x, detail: e.target.value } : x)))}
              placeholder={`Descreva: ${t.label} *`} className="h-9" data-testid={`trace-detail-${t.type_id}`} />
          ))}
          {traces.length > 0 && (
            <>
              <PhotoInput id="traces-photos" onFiles={addTracesPhotos} label="Fotos dos vestígios (opcional)" />
              <PhotoStrip photos={tracesPhotos} onRemove={(i) => setTracesPhotos((p) => p.filter((_, j) => j !== i))} />
              <Textarea rows={2} placeholder="Observações sobre os vestígios..." className="resize-none text-sm"
                value={tracesNotes} onChange={(e) => setTracesNotes(e.target.value)} data-testid="traces-notes" />
            </>
          )}
        </div>
      </Card>

      <Card className="p-4 border-border bg-card space-y-2.5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm font-medium">Fotos gerais da sala</p>
          <PhotoInput id="general-photos" onFiles={addGeneralPhotos} />
        </div>
        <PhotoStrip photos={generalPhotos} onRemove={(i) => setGeneralPhotos((p) => p.filter((_, j) => j !== i))} />
        <Textarea rows={2} placeholder="Observações gerais da inspeção..." className="resize-none text-sm"
          value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="inspection-notes" />
      </Card>

      <Button className="w-full" size="lg" onClick={submit} disabled={submitting} data-testid="inspection-submit-btn">
        {submitting ? "Registrando..." : idx + 1 < rooms.length ? (
          <>Concluir sala e avançar para a próxima <ChevronRight size={16} className="ml-1" /></>
        ) : (
          <>Concluir última sala e finalizar <CheckCircle2 size={16} className="ml-1" /></>
        )}
      </Button>
    </div>
  );
}